import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { useIsMutating, useMutation } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { CornerConnectionAdditionalLoadProposalStates, useResultsStore } from '@editorStores'
import { useSelectionMode } from '@editorHooks'
import { cornerConnectionProposal, cornerConnectionProposalWithInterval } from '@mutations'
import { buildErrorMessage } from 'src/constants'

const selectionKey = 'select-wall-to-connect-proposal'
const getProposalKey = ['get-proposal']

const useCornerConnectionAdditionalLoadProposalState = () => {
  const { projectId } = useParams()

  const { enqueueSnackbar } = useSnackbar()

  const { unsetSelectionMode, setSelectionMode, isSelectionMode } = useSelectionMode()

  const setCornerConnectionAdditionalLoadProposal = useResultsStore(
    state => state.setCornerConnectionAdditionalLoadProposal,
  )

  const cornerConnectionState = useResultsStore(
    state => state.cornerConnectionAdditionalLoadProposal,
  )
  const setSelectedProposalAnchor = useResultsStore(state => state.setSelectedProposalAnchor)

  const isMutating = useIsMutating(getProposalKey)

  const abortHelper = useCallback(() => {
    setCornerConnectionAdditionalLoadProposal({
      type: 'idle',
    })
    unsetSelectionMode()
  }, [setCornerConnectionAdditionalLoadProposal, unsetSelectionMode])

  useEffect(() => {
    if (cornerConnectionState.type === 'selected-anchor' && !isSelectionMode) {
      abortHelper()
    }
  }, [isSelectionMode, cornerConnectionState.type, abortHelper])

  const selectAnchor = useCallback(
    (anchorGuid: string, field = '') => {
      setCornerConnectionAdditionalLoadProposal({
        type: 'selected-anchor',
        anchorGuid,
        field: field,
      })

      setSelectionMode({
        key: selectionKey,
        message:
          'Wähle eine Wand an, um die zusätzlich Auflast bei einer Verschraubung zu vermitteln',
      })
    },
    [setCornerConnectionAdditionalLoadProposal, setSelectionMode],
  )

  const selectWall = useCallback(
    (connectedWallGuid: string) => {
      if (cornerConnectionState.type !== 'selected-anchor') return

      const state: CornerConnectionAdditionalLoadProposalStates = {
        ...cornerConnectionState,
        type: 'selected-anchor-and-wall',
        wallGuid: connectedWallGuid,
      }
      setCornerConnectionAdditionalLoadProposal(state)

      return state
    },
    [cornerConnectionState, setCornerConnectionAdditionalLoadProposal],
  )

  const changeProposal = useCallback(
    (intervalStart: number, intervalEnd: number, interval: 'left' | 'right' = 'left') => {
      if (
        cornerConnectionState.type !== 'proposal-created' &&
        cornerConnectionState.type !== 'proposal-changed'
      )
        return

      const intervalData =
        interval === 'left'
          ? { left_interval: { lower: intervalStart, upper: intervalEnd } }
          : { right_interval: { lower: intervalStart, upper: intervalEnd } }

      setCornerConnectionAdditionalLoadProposal({
        ...cornerConnectionState,
        type: 'proposal-changed',
        proposal: {
          ...cornerConnectionState.proposal,
          ...intervalData,
        },
      })
    },
    [cornerConnectionState, setCornerConnectionAdditionalLoadProposal],
  )

  const clearProposalInterval = useCallback(
    (interval: 'left' | 'right') => {
      if (
        cornerConnectionState.type !== 'proposal-created' &&
        cornerConnectionState.type !== 'proposal-changed'
      )
        return

      const intervalData =
        interval === 'left' ? { left_interval: undefined } : { right_interval: undefined }

      setCornerConnectionAdditionalLoadProposal({
        ...cornerConnectionState,
        type: 'proposal-changed',
        proposal: {
          ...cornerConnectionState.proposal,
          ...intervalData,
        },
      })
    },
    [cornerConnectionState, setCornerConnectionAdditionalLoadProposal],
  )

  const { mutateAsync } = useMutation(
    async (data: { anchorGuid: string; wallGuid: string; field: string }) => {
      const result = await cornerConnectionProposal.request(
        projectId as string,
        data.anchorGuid,
        data.wallGuid,
      )

      return {
        data: result,
        field: data.field,
        wallGuid: data.wallGuid,
        anchorGuid: data.anchorGuid,
      }
    },
    {
      mutationKey: getProposalKey,
      onSuccess: result => {
        setCornerConnectionAdditionalLoadProposal({
          type: 'proposal-created',
          proposal: result.data,
          field: result.field,
          wallGuid: result.wallGuid,
          anchorGuid: result.anchorGuid,
        })
        setSelectedProposalAnchor(result.anchorGuid)
      },
      onError: (error: AxiosError) => {
        // Leave the 'selected-anchor-and-wall' state, otherwise the helper keeps showing its
        // loading indicator and blocks the anchor form (e.g. wall too far away → 422)
        abortHelper()
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden'), {
          variant: 'error',
          persist: true,
        })
      },
    },
  )

  const { mutateAsync: mutateAsyncWithInterval, isLoading: isLoadingWithInterval } = useMutation(
    async (data: {
      anchorGuid: string
      wallGuid: string
      leftInterval?: Interval
      rightInterval?: Interval
    }) => {
      const result = await cornerConnectionProposalWithInterval.request(
        projectId as string,
        data.anchorGuid,
        data.wallGuid,
        data.leftInterval,
        data.rightInterval,
      )
      return {
        data: result,
      }
    },
    {
      onSuccess: result => {
        if (
          cornerConnectionState.type !== 'proposal-changed' &&
          cornerConnectionState.type !== 'proposal-created'
        )
          return

        const newState: CornerConnectionAdditionalLoadProposalStates = {
          ...cornerConnectionState,
          type: 'proposal-created',
          proposal: result.data,
        }

        setCornerConnectionAdditionalLoadProposal(newState)
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden'), {
          variant: 'error',
          persist: true,
        })
      },
    },
  )

  const fetchWithInterval = useCallback(() => {
    if (
      cornerConnectionState.type !== 'proposal-changed' &&
      cornerConnectionState.type !== 'proposal-created'
    )
      return

    const leftAsDecimal = cornerConnectionState.proposal.left_interval
      ? {
          lower: cornerConnectionState.proposal.left_interval.lower.toString(),
          upper: cornerConnectionState.proposal.left_interval.upper.toString(),
        }
      : undefined

    const rightAsDecimal = cornerConnectionState.proposal.right_interval
      ? {
          lower: cornerConnectionState.proposal.right_interval.lower.toString(),
          upper: cornerConnectionState.proposal.right_interval.upper.toString(),
        }
      : undefined

    mutateAsyncWithInterval({
      anchorGuid: cornerConnectionState.anchorGuid,
      wallGuid: cornerConnectionState.wallGuid,
      leftInterval: leftAsDecimal,
      rightInterval: rightAsDecimal,
    })
  }, [cornerConnectionState, mutateAsyncWithInterval])

  return {
    selectAnchor,
    cornerConnectionState,
    isLoadingProposal: isMutating,
    abortHelper,
    changeProposal,
    fetchWithInterval,
    isLoadingWithInterval,
    proposalSelectionKey: selectionKey,
    clearProposalInterval,
    mutateAsync,
    selectWall,
  }
}

export default useCornerConnectionAdditionalLoadProposalState
